import { Footer } from "@growth/dweb-footer";
import { getAnalytics } from "@tm/insurance/analytics";
import {
  initializePageTracking,
  trackEvent,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import { navigationActions } from "@tm/insurance/navigation";
import { getCookieByName, replaceHistory } from "@tm/insurance/utils";
import { PARTNER } from "@tm/isomorphic-utils";
import {
  // WhyBuyTravIns,
  ContentBlock,
  // BottomBar,
  ExclusiveOffers,
  InsuranceStats,
  // LandingFooter,
  SafenSecureTrav,
  WhatsCovered,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import dynamic from "next/dynamic";

import { useContext, useEffect } from "react";
import CommonLandingDT from "../../components/CommonLandingDT/CommonLandingDT";
import QnABlock from "../../components/QnABlock/QnABlock";
// import TravelProtection from "../../components/TravelProtection/TravelProtection";
import { CommonLandingPageProps } from "../InsuranceCommonLanding/types";
import { GiFooterWrap } from "../InsuranceLanding/InsuranceLanding.style";
import {
  CommonDesktopLayoutContainer,
  CommonLandingPwaWrapper,
} from "./InsuranceCommonLanding.style";

// Code-split components with SSR enabled (for desktop)
const ClaimYourInsurance = dynamic(
  () => import("@tm/static").then((mod) => mod.ClaimYourInsurance),
  { ssr: true }
);

const BottomBar = dynamic(
  () => import("../../components/BottomBar/BottomBar"),
  { ssr: true }
);

const LandingFooter = dynamic(
  () => import("@tm/static").then((mod) => mod.LandingFooter),
  { ssr: true }
);

const WhyBuyTravIns = dynamic(
  () => import("@tm/static").then((mod) => mod.WhyBuyTravIns),
  { ssr: true }
);

const TravelProtection = dynamic(
  () => import("../../components/TravelProtection/TravelProtection"),
  { ssr: true }
);

const LandingTestimonials = dynamic(
  () => import("@tm/static").then((mod) => mod.LandingTestimonials),
  { ssr: true }
);

const SeoContent = dynamic(
  () =>
    import("@insurance/landing-ui/components").then((mod) => mod.SeoContent),
  { ssr: true }
);

// Lazy-loaded PWA/mobile components (client-side only)
const HeroSection = dynamic(
  () => import("../../components/HeroSection/HeroSection"),
  { ssr: false }
);

const HeaderCard = dynamic(
  () => import("../../components/HeaderCard/HeaderCard"),
  { ssr: false }
);

const TravelPlans = dynamic(
  () => import("../../components/TravelPlans/TravelPlans"),
  { ssr: false }
);

const giFooterData = [
  {
    title: "Popular Flight Sectors",
    links: [
      {
        text: "Kolkata to Delhi Flight",
        url: "https://www.goibibo.com/flights/kolkata-to-delhi-flights/",
      },
      {
        text: "Hyderabad to Delhi Flight",
        url: "https://www.goibibo.com/flights/hyderabad-to-delhi-flights/",
      },
    ],
  },
];

const analytics = getAnalytics();

const InsuranceCommonLanding = ({
  data,
  partnerType,
  setActiveModal,
  query,
}: CommonLandingPageProps) => {
  const experiment = useContext(ExperimentTrackingString) || "";

  const { setType, type } = usePlanType();

  const {
    travelProtection,
    desktopLayout,
    travelInsurance,
    heading,
    offers,
    footer: footerInfoData,
    pokusExperiments,
    bottomBarInfo,
    qnABlockInfo,
    seoContent,
    claimYourInsurance,
    testimonials,
    contentBlockInfo,
    countryCode,
    seoPageType,
    segmentId,
    qnaBlockTitle,
    faq: faqData,
  } = data || {};

  const device = useBreakpointV2();

  const footerInfo = footerInfoData || []; // || LOCAL_FOOTER_INFO;
  //TODO: move this Logic to the LandingFooter component and remove below duplicate code from wherever it's getting used
  const footerLinks =
    (footerInfo as any[])
      ?.filter((data: any) => data.type === "section")
      ?.map((item: any, index: any) => ({
        id: index.toString(),
        label: item.title || "",
        listPoints:
          item?.links?.map((link: any, i: any) => ({
            id: i.toString(),
            label: link.title || "",
            link: link.url || "",
          })) ?? [],
        icon: "",
      })) ?? [];

  const footerHeading = (footerInfo as any[])?.find(
    (data: any) => data.type === "rich-text"
  );
  const footnoteData = (footerInfo as any[])?.find(
    (data: any) => data.type === "footnote"
  );
  const footerCopyrightData =
    footnoteData && "items" in footnoteData
      ? {
          type: footnoteData.type,
          items: footnoteData.items?.length
            ? ([footnoteData.items[0]] as [
                {
                  type?: string;
                  value?: string;
                  links?: any;
                }
              ])
            : undefined,
        }
      : undefined;

  useEffect(() => {
    if (desktopLayout?.travelFormInfo?.travellerInfoSection) {
      replaceHistory("isRetail", "true");
      analytics?.trackPageLoadEvent(
        {
          pageName: "COMMON_LANDING_RETAIL",
          experiment,
        },
        type
      );
    } else {
      analytics?.trackPageLoadEvent(
        { pageName: "COMMON_LANDING", experiment },
        type
      );
    }
    const queryParams = new URLSearchParams(window.location.search);
    const { value = [] } = desktopLayout?.travelFormInfo?.countryList || {};
    const selectedCountriesForQuery =
      query?.countries || queryParams.get("countries");
    const selectedCountryCodes = selectedCountriesForQuery?.split(",");
    const subpageName = queryParams.get("type") || query?.type;

    let totalCountryData = value?.map((data: any) => {
      if (selectedCountryCodes?.includes(data.code)) {
        return { ...data, isSelected: true };
      }
      return { ...data };
    });
    const selectedCountry = totalCountryData?.filter(
      (item: any) => item.isSelected
    );
    if (value?.length) {
      localStorage.setItem("TRAVEL_INSURANCE_COUNTRIES", JSON.stringify(value));
    }
    initializePageTracking(
      {
        page_context: {
          page_name: "common_landing",
          funnel_step: "landing",
          page_type: "landing",
          ...(subpageName && { subpage_name: subpageName }),
        },
        event_tracking_context: {
          funnel_entry: "direct",
        },
      },
      {
        countries: selectedCountry ? selectedCountry : [],
        journeyType: query?.segment || queryParams.get("segment"),
        travellers: parseInt(
          query?.travellers || queryParams.get("travellers") || "1"
        ),
        fromDateTime:
          query?.startDate || queryParams.get("startDate")
            ? parseInt(query?.startDate || queryParams.get("startDate") || "0")
            : 0,
        toDateTime:
          query?.endDate || queryParams.get("endDate")
            ? parseInt(query?.endDate || queryParams.get("endDate") || "0")
            : 0,
      }
    );
    return () => {
      triggerPageExit();
    };
  }, []);

  const handleTravelInsuranceClick = () => {
    analytics?.trackCommonLandingEvents({
      actionType: "travel_insurance_card_clicked",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "travel_insurance_card_clicked",
      },
    });
    setType(undefined);
    navigationActions.navigateToLanding({ type: "" }, false);
  };

  const goToMyOrders = () => {
    analytics?.trackCommonLandingEvents(
      {
        actionType: "my_orders_clicked",
      }
      //"click"
    );

    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "my_orders_clicked",
      },
    });
    triggerPageExit();

    const authCookie = getCookieByName("mmt-auth");

    if (authCookie) {
      navigationActions.navigateToOrderListing();
    } else {
      setActiveModal?.("LOGIN");
    }
  };

  return (
    <div id="pageId">
      {device === "lg" ? (
        <CommonDesktopLayoutContainer>
          <CommonLandingDT
            data={desktopLayout}
            countryCode={countryCode}
            seoPageType={seoPageType}
            segmentId={segmentId}
            query={query}
            pokus={pokusExperiments}
          />
          <TravelProtection
            title={travelProtection?.title}
            plans={travelProtection?.plans}
          />

          {desktopLayout?.pageInfo?.customNumbers && (
            <InsuranceStats data={desktopLayout.pageInfo.customNumbers} />
          )}

          {offers && <ExclusiveOffers data={offers} />}

          {desktopLayout?.pageInfo?.TravelInsData && (
            <WhyBuyTravIns
              title={desktopLayout.pageInfo.TravelInsData.title}
              info={desktopLayout.pageInfo.TravelInsData.info}
              data={desktopLayout.pageInfo.TravelInsData.featureCard}
            />
          )}
          {desktopLayout?.pageInfo?.safeSecureTravel && (
            <SafenSecureTrav
              data={desktopLayout.pageInfo.safeSecureTravel.safeTravCrd}
              title={desktopLayout.pageInfo.safeSecureTravel.title}
            />
          )}

          {desktopLayout?.pageInfo?.whatsCovered && (
            <WhatsCovered
              data={desktopLayout.pageInfo.whatsCovered.travCrd}
              title={desktopLayout.pageInfo.whatsCovered.title}
              info={desktopLayout.pageInfo.whatsCovered.info}
            />
          )}
          {claimYourInsurance && (
            <ClaimYourInsurance
              title={claimYourInsurance.title}
              info={claimYourInsurance.info}
              data={claimYourInsurance.claimData}
            />
          )}

          {testimonials && (
            <LandingTestimonials
              customTitle={testimonials?.title}
              test={testimonials?.testimonialData}
            />
          )}

          {seoContent && <SeoContent seoContent={seoContent} />}
          <LandingFooter
            footerData={footerLinks}
            footerHeading={footerHeading}
            footerCopyrightData={footerCopyrightData}
            footerTheme="light"
          />
          {contentBlockInfo && contentBlockInfo?.length > 0 && (
            <ContentBlock data={contentBlockInfo} />
          )}

          <QnABlock data={faqData?.faqList} qnaBlockTitle={faqData?.title} />

          {partnerType !== PARTNER.GI && <BottomBar data={bottomBarInfo} />}
          {partnerType === PARTNER.GI && (
            <GiFooterWrap>
              <Footer data={giFooterData} adobe="" />
            </GiFooterWrap>
          )}
        </CommonDesktopLayoutContainer>
      ) : (
        <CommonLandingPwaWrapper>
          <HeroSection heading={heading} goToMyOrders={goToMyOrders} />
          {travelInsurance && (
            <HeaderCard
              data={travelInsurance}
              onClick={handleTravelInsuranceClick}
            />
          )}
          {travelProtection && (
            <TravelPlans travelProtection={travelProtection} />
          )}
          {offers && (
            <ExclusiveOffers
              data={offers}
              bgColor={theme.whiteText2}
              padding="0"
            />
          )}
        </CommonLandingPwaWrapper>
      )}
    </div>
  );
};

export default InsuranceCommonLanding;
